import { useState } from 'react';
import { X } from 'lucide-react';
import { toast } from 'sonner';
import { useLang } from '../i18n';
import { api } from '../api';
import { cn } from '../lib/utils';
import { useAuth } from '../AuthContext';

export default function StudentModal({ student, onClose, onSaved }) {
  const { t } = useLang();
  const isEdit = !!student?.id;
  const { user } = useAuth();  // v2.29 : notes privées réservées à l'admin

  const [form, setForm] = useState({
    first_name: student?.first_name || '',
    last_name: student?.last_name || '',
    email: student?.email || '',
    phone: student?.phone || '',
    instagram: student?.instagram || '',
    dance_background: student?.dance_background || '',
    experience_years: student?.experience_years || 0,
    notes: student?.notes || '',
    role: student?.role || null,
  });
  const [saving, setSaving] = useState(false);

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  const handleSave = async () => {
    if (!form.first_name || !form.last_name) { toast.error('Prénom et nom requis'); return; }
    setSaving(true);
    let saved = null;
    try {
      if (isEdit) {
        await api.put(`/students/${student.id}`, form);
        toast.success('Élève mis à jour');
      } else {
        saved = await api.post('/students', form);
        toast.success('Élève ajouté');
      }
      onSaved(saved);  // v2.29 : la fiche créée (utilisée par l'ajout à un cours)
    } catch (e) {
      toast.error(e.message || 'Erreur');
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-end md:items-center justify-center p-0 md:p-4" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="bg-white w-full md:max-w-md rounded-t-3xl md:rounded-2xl shadow-2xl max-h-[90vh] flex flex-col overflow-hidden" data-testid="student-modal">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100 shrink-0">
          <h2 className="font-bold text-lg text-slate-900 font-manrope">
            {isEdit ? 'Modifier l\'élève' : 'Nouvel élève'}
          </h2>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-full bg-slate-100 hover:bg-slate-200 transition-colors" data-testid="student-modal-close">
            <X size={16} />
          </button>
        </div>

        <div className="overflow-y-auto flex-1 px-5 py-4 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Prénom *">
              <input value={form.first_name} onChange={set('first_name')} className="input-sm" placeholder="Marie" data-testid="firstname-input" />
            </Field>
            <Field label="Nom *">
              <input value={form.last_name} onChange={set('last_name')} className="input-sm" placeholder="Dupont" data-testid="lastname-input" />
            </Field>
          </div>
          <Field label={t('common.email')}>
            <input type="email" value={form.email} onChange={set('email')} className="input-sm" placeholder="marie@example.com" data-testid="email-input" />
          </Field>
          <Field label={t('common.phone')}>
            <input type="tel" value={form.phone} onChange={set('phone')} className="input-sm" placeholder="+41 79 000 00 00" data-testid="phone-input" />
          </Field>
          <Field label={t('common.instagram')}>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">@</span>
              <input value={form.instagram} onChange={set('instagram')} className="input-sm pl-7" placeholder="marie.dance" data-testid="instagram-input" />
            </div>
          </Field>
          <Field label={t('students.background')}>
            <input value={form.dance_background} onChange={set('dance_background')} className="input-sm" placeholder="Bachata, Zouk..." data-testid="background-input" />
          </Field>
          <Field label={t('students.exp')}>
            <input type="number" min="0" value={form.experience_years} onChange={set('experience_years')} className="input-sm" data-testid="exp-input" />
          </Field>
          {user?.role === 'admin' && (
            <Field label={t('students.notes')}>
              <textarea value={form.notes} onChange={set('notes')} rows={3} className="input-sm resize-none" placeholder="Observations..." data-testid="notes-input" />
            </Field>
          )}
          <div>
            <label className="block text-xs font-bold text-slate-600 uppercase tracking-wider mb-1.5">Rôle par défaut</label>
            <div className="flex gap-2">
              {[
                { value: 'leader', label: 'Leader', active: 'bg-blue-500 text-white', inactive: 'bg-slate-100 text-slate-600 hover:bg-slate-200' },
                { value: 'follower', label: 'Follower', active: 'bg-pink-500 text-white', inactive: 'bg-slate-100 text-slate-600 hover:bg-slate-200' },
              ].map(r => (
                <button
                  key={r.value}
                  type="button"
                  onClick={() => setForm(f => ({ ...f, role: f.role === r.value ? null : r.value }))}
                  className={cn('flex-1 py-2 rounded-lg text-sm font-semibold transition-colors', form.role === r.value ? r.active : r.inactive)}
                  data-testid={`role-${r.value}-btn`}
                >
                  {r.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="px-5 pb-5 pt-3 border-t border-slate-100 shrink-0 flex gap-3">
          <button onClick={onClose} className="flex-1 h-12 rounded-xl bg-slate-100 text-slate-700 font-bold text-sm">{t('common.cancel')}</button>
          <button onClick={handleSave} disabled={saving} className="flex-1 h-12 rounded-xl bg-[#0288D1] text-white font-bold text-sm hover:bg-[#0277BE] active:scale-95 transition-all" data-testid="save-student-btn">
            {saving ? '...' : t('common.save')}
          </button>
        </div>
      </div>

      <style>{`
        .input-sm {
          width: 100%;
          height: 2.5rem;
          padding: 0 0.75rem;
          background: #f8fafc;
          border: 1px solid #e2e8f0;
          border-radius: 0.5rem;
          font-size: 0.875rem;
          outline: none;
          transition: all 0.15s;
        }
        .input-sm:focus {
          border-color: #0288D1;
          box-shadow: 0 0 0 3px rgba(2, 136, 209, 0.1);
          background: white;
        }
        .input-sm.pl-7 { padding-left: 1.75rem; }
        textarea.input-sm { height: auto; padding-top: 0.5rem; padding-bottom: 0.5rem; }
      `}</style>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-xs font-bold text-slate-600 uppercase tracking-wider mb-1.5">{label}</label>
      {children}
    </div>
  );
}
